import { mapGetters, mapMutations } from 'vuex';
import {
  BASE_MEETING_PLAN_TYPES,
  BILLING_CYCLE,
  planTypes,
  STAND_ALONE_PLAN_TYPES,
} from '@/common/constants';
import _ from 'lodash';
import { plansof } from '@/utils/chain'
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { webinarSingleUseAllowed } from '@/common/service/op-switch';

const MEETING_PLAN_TYPES = [...BASE_MEETING_PLAN_TYPES, planTypes.ZOOM_ROOMS, planTypes.ZOOM_ONE];
export default {
  inject: ['$core'],
  computed: {
    ...mapGetters(['getSelectedPlans', 'planMapFlat', 'selectedSingleUsePro', 'getBoughtBasePlanInfo', 'boughtPlans']),
    selectedWebinarPlans() {
      return this.getSelectedPlans.filter(p => p.type === planTypes.WEBINAR);
    },
    maxCapacityLicense() {
      return Math.max(...this.selectedWebinarPlans.filter(p => p.billing_cycle !== BILLING_CYCLE.ONE_TIME).map(p => p.cartInfo.selected_number));
    },
    maxOneTimeLicense() {
      return this.oneTimeWebinar.map(p => p.cartInfo.selected_number).reduce((a,b) => a + b, 0);
    },
    isAllOneTimeWebinar() {
      return this.selectedWebinarPlans.length && this.selectedWebinarPlans.every(p => p.billing_cycle === BILLING_CYCLE.ONE_TIME);
    },
    oneTimeWebinar() {
      return this.selectedWebinarPlans.filter(p => p.billing_cycle === BILLING_CYCLE.ONE_TIME);
    },
    oneTimeWebinarCodeMap() {
      return this.oneTimeWebinar.reduce((prev,cur) => {
        prev[cur.plan_code] = cur.cartInfo.selected_number;
        return prev;
      }, {})
    },
    selectedMeetingPlan() {
      return this.getSelectedPlans.find(p => p.type === planTypes.ZOOM_MEETING);
    },
    selectedCommonMeetingPlan() {
      return this.getSelectedPlans.find(p => MEETING_PLAN_TYPES.includes(p.type) && p.billing_cycle !== BILLING_CYCLE.ONE_TIME);
    },
    isSelectedEduPlan() {
      return this.getSelectedPlans.find(p => p.type === planTypes.ZOOM_MEETING && p.category === ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP);
    },
    boughtCommonMeetingPlan() {
      return this.boughtPlans.find(p => MEETING_PLAN_TYPES.includes(p.type) && p.billing_cycle !== BILLING_CYCLE.ONE_TIME && !p.isSingleUseProFreeTrial);
    },
    singleUsePro() {
      return this.planMapFlat.find(p => p.billing_cycle === BILLING_CYCLE.ONE_TIME && p.type === planTypes.ZOOM_MEETING);
    },
    eventServicePlans() {
      return this.getSelectedPlans.filter(p => p.type === planTypes.EVENT_SERVICES);
    },
    mx_isPaidStandalone() {
      if (!this.getBoughtBasePlanInfo) return false
      return STAND_ALONE_PLAN_TYPES.includes(this.getBoughtBasePlanInfo.planType)
    },
    mainPlanIsWebinar() {
      const filterKey = this.$core?.draftPlanFilterKeys?.[0];
      return filterKey === planTypes[planTypes.WEBINAR] || filterKey === planTypes.WEBINAR;
    },
    isAdd2CartFlowAndCanAutoAdd() {
      return this.$context.is.add2cart && this.mx_canAutoAdd;
    }
  },
  watch: {
    getSelectedPlans() {
      if (!webinarSingleUseAllowed) return;
      if (this.$context.type !== planTypes.WEBINAR) return;
      if (MEETING_PLAN_TYPES.includes(this.$context.type)) return;
      if (!this.mainPlanIsWebinar) return;
      if (this.isSelectedEduPlan) return;
      this.$nextTick(() => {
        if (this.isAllOneTimeWebinar) {
          if (this.mx_isPaidStandalone) {
            // standalone, must be at least one Pro
            if (!this.selectedCommonMeetingPlan && !this.boughtCommonMeetingPlan) {
              this.mx_loadPro(1)
            }
          } else {
            if (this.selectedSingleUsePro) return;
            if (this.selectedCommonMeetingPlan && !this.isAdd2CartFlowAndCanAutoAdd) {
              // remove paid pro first
              this.removePlan(this.selectedCommonMeetingPlan.plan_code);
            }
            if (!this.boughtCommonMeetingPlan) {
              // load single use pro
              this.updateSelectPlan({
                plan_code: this.singleUsePro.plan_code,
                cartInfo: {
                  selected_number: this.maxOneTimeLicense,
                },
              })
            }
          }
        } else {
          if (this.selectedCommonMeetingPlan || this.boughtCommonMeetingPlan) return;
          // remove single use pro
          const singleUsePro = this.getSelectedPlans.find(p => p.billing_cycle === BILLING_CYCLE.ONE_TIME && p.type === planTypes.ZOOM_MEETING);
          this.removePlan(singleUsePro.plan_code);
          // load paid pro
          this.mx_loadPro(this.maxCapacityLicense)
        }
      });
    },
    maxOneTimeLicense: {
      immediate: true,
      handler() {
        if (!webinarSingleUseAllowed) return;
        // add single use pro
        if (this.isAllOneTimeWebinar && !this.selectedCommonMeetingPlan && !this.isSelectedEduPlan && !this.boughtCommonMeetingPlan) {
          this.updateSelectPlan({
            plan_code: this.singleUsePro.plan_code,
            cartInfo: {
              selected_number: this.maxOneTimeLicense,
            },
          });
        }
      }
    },
    oneTimeWebinarCodeMap: {
      immediate: true,
      deep: true,
      handler(nv, ov) {
        if (!webinarSingleUseAllowed) return;
        const _nv = Object.entries(nv || []);
        const _ov = Object.entries(ov || []);
        if (!_nv.length && !_ov.length) return;
        this.$nextTick(() => {
          if (_nv.length) {
            if (_.isEqual(nv, ov)) return;
            const nvDiff = Object.keys(nv).reduce((p,key) => {
              const quantity = nv[key];
              if (ov?.[key] === quantity) return p;
              p.push([key, quantity]);
              return p;
            }, []);
            nvDiff.forEach(([plan_code]) => {
              const plan = this.oneTimeWebinar.find(p => p.plan_code === plan_code);
              this.updateSelectPlan({
                plan_code: plan?.esmd_plancode,
                cartInfo: plan.cartInfo,
              });
            });

            this.eventServicePlans.forEach(p => {
              if (this.oneTimeWebinar.find(plan => plan.esmd_plancode === p.plan_code)) return;
              this.removePlan(p.plan_code);
            });

            this.$context.$emit('update-draft-plan-keys');
          } else {
            this.batchRemovePlan(this.eventServicePlans);
          }
        });
      }
    }
  },

  methods: {
    ...mapMutations(['removePlan', 'updateSelectPlan', 'batchRemovePlan']),
    mx_loadPro(num) {
      const proPlan = plansof(this.planMapFlat).type(planTypes.ZOOM_MEETING).annual().get(0)
      if (proPlan) {
        this.updateSelectPlan({
          plan_code: proPlan.plan_code,
          cartInfo: {
            selected_number: num,
          },
        })
      }
    },
  },
}
